/**
 * HiddenOpportunities — "Jobs where you are unusually qualified"
 *
 * Shows jobs where skills strongly match but the current title does NOT —
 * career pivot opportunities that ordinary job boards miss.
 */
import { ArrowRight, Sparkles } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface HiddenOpportunity {
  id: string;
  fromTitle: string;
  toTitle: string;
  company: string;
  score: number;
  location?: string;
}

interface HiddenOpportunitiesProps {
  currentTitle?: string;
  opportunities: HiddenOpportunity[];
  onViewJob?: (jobId: string) => void;
  onViewAll?: () => void;
  /** Show demo/empty hint when no live opportunities */
  showPlaceholder?: boolean;
  demoMode?: boolean;
  className?: string;
}

function scoreColor(score: number): string {
  if (score >= 85) return '#22c55e';
  if (score >= 70) return 'var(--ds-marketing-accent, #5F8568)';
  return '#f59e0b';
}

const HiddenOpportunities: React.FC<HiddenOpportunitiesProps> = ({
  currentTitle,
  opportunities,
  onViewJob,
  onViewAll,
  showPlaceholder = false,
  demoMode = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const hasLive = opportunities.length > 0;

  if (!hasLive && !showPlaceholder) return null;

  return (
    <section
      className={`ds-hidden-opportunities ${className}`.trim()}
      aria-labelledby="hidden-opportunities-title"
    >
      <div className="ds-hidden-opportunities__glow" aria-hidden />

      <header className="ds-hidden-opportunities__header">
        <span className="ds-hidden-opportunities__emoji" aria-hidden>💎</span>
        <div className="min-w-0">
          <h2 id="hidden-opportunities-title" className="ds-hidden-opportunities__title">
            {t('dashboard.hiddenOps.sectionTitle', 'Jobs where you are unusually qualified')}
          </h2>
          <p className="ds-hidden-opportunities__subtitle">
            {t('dashboard.hiddenOps.subtitle', "You may be qualified for these jobs even though your current title doesn't match.")}
          </p>
        </div>
        {demoMode && (
          <span className="ds-hidden-opportunities__demo-badge">
            {t('dashboard.hiddenOps.demoBadge', 'AI semantic matching')}
          </span>
        )}
      </header>

      {hasLive ? (
        <ul className="ds-hidden-opportunities__list">
          {opportunities.map((opp) => (
            <li key={opp.id}>
              <button
                type="button"
                onClick={() => onViewJob?.(opp.id)}
                className="ds-hidden-opp-row touch-manipulation"
                disabled={!onViewJob || demoMode}
              >
                <p className="ds-hidden-opp-row__transition">
                  <span className="ds-hidden-opp-row__from">{opp.fromTitle || currentTitle}</span>
                  <ArrowRight className="w-3.5 h-3.5 shrink-0 ds-hidden-opp-row__arrow" aria-hidden />
                  <span className="ds-hidden-opp-row__to">{opp.toTitle}</span>
                  <span className="ds-hidden-opp-row__dash" aria-hidden>—</span>
                  <span className="ds-hidden-opp-row__score t-num" style={{ color: scoreColor(opp.score) }}>
                    {opp.score}%
                  </span>
                </p>
                <p className="ds-hidden-opp-row__company t-meta">
                  {opp.company}
                  {opp.location ? ` · ${opp.location}` : ''}
                </p>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="ds-hidden-opportunities__empty">
          <p className="t-meta">
            {currentTitle
              ? t('dashboard.hiddenOps.emptyWithTitle', 'We are still scanning for pivot roles that fit your skills. Check back after your next match refresh.')
              : t('dashboard.hiddenOps.emptyNoTitle', 'Add your current role in Settings → Profile to unlock hidden career paths.')}
          </p>
        </div>
      )}

      {onViewAll && hasLive && (
        <footer className="ds-hidden-opportunities__footer">
          <button
            type="button"
            onClick={onViewAll}
            className="ds-btn ds-btn-ghost ds-btn-sm w-full touch-manipulation"
          >
            {t('dashboard.hiddenOps.viewAll', 'Explore all hidden opportunities')}
            <Sparkles className="w-3.5 h-3.5 ml-1" style={{ color: '#a855f7' }} aria-hidden />
          </button>
        </footer>
      )}
    </section>
  );
};

export default React.memo(HiddenOpportunities);
